import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import en from "../i18n/en.json";
import { LANGUAGES, RTL_LANGUAGES, DEFAULT_LANGUAGE } from "../i18n/languages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = { en };

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const seg = window.location.pathname.split("/")[1] || "";
    if (LANGUAGES.some((l) => l.code === seg)) return seg;
    return localStorage.getItem("lm_lang") || null;
  });
  const [source, setSource] = useState(null);
  const [t, setT] = useState(en);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (lang) return;
    let cancelled = false;
    axios
      .get(`${API}/detect-language`, { timeout: 5000 })
      .then((res) => {
        if (cancelled) return;
        const detected = res.data?.language;
        const valid = LANGUAGES.some((l) => l.code === detected) ? detected : DEFAULT_LANGUAGE;
        setLang(valid);
        setSource(res.data?.source || "auto");
      })
      .catch(() => {
        if (cancelled) return;
        const nav = (navigator.language || "en").split("-")[0].toLowerCase();
        setLang(LANGUAGES.some((l) => l.code === nav) ? nav : DEFAULT_LANGUAGE);
        setSource("browser");
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    if (!lang) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (!cache[lang]) {
          const mod = await import(`../i18n/${lang}.json`);
          cache[lang] = mod.default;
        }
        if (!cancelled) setT({ ...en, ...cache[lang] });
      } catch {
        if (!cancelled) setT(en);
      } finally {
        if (!cancelled) {
          localStorage.setItem("lm_lang", lang);
          document.documentElement.lang = lang;
          document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? "rtl" : "ltr";
          setReady(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const setLanguage = useCallback((code) => {
    localStorage.setItem("lm_lang", code);
    setSource("manual");
    setLang(code);
  }, []);

  return (
    <LanguageContext.Provider
      value={{ lang: lang || DEFAULT_LANGUAGE, t, setLanguage, source, ready, languages: LANGUAGES }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
